/**
 * Fase 5.0 — Dashboard Operasional Absensi & Lembur.
 * Read-only monitoring: KPI Absensi Hari Ini, KPI Lembur Hari Ini,
 * dan Laporan per Lokasi. Semua scope security enforce di backend
 * via `GET /api/operations/dashboard` — frontend TIDAK melakukan
 * filtering scope, hanya menampilkan payload dari backend.
 *
 * Read-only page: tidak boleh ada tombol Koreksi/Approve/Delete —
 * quick-action hanya deep-link ke Rekap Absensi / Rekap Lembur /
 * halaman Pengajuan / Rekap Lembur PERLU_VERIFIKASI.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCcw, Users, Clock, AlertTriangle, CheckCircle2, XCircle, LogIn, LogOut, FileText, Loader2, MapPin, Timer, ChevronRight, UserCheck, HeartPulse, CalendarDays, PlayCircle, AlarmClock, Ban, ArrowRight, Wrench, UserCog, ClipboardList } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ATT_LABEL = {
  HADIR: "Hadir", TERLAMBAT: "Terlambat", IZIN: "Izin", SAKIT: "Sakit",
  CUTI: "Cuti", ALPA: "ALPA", PERLU_VERIFIKASI: "Perlu Verifikasi",
  BELUM_CHECKIN: "Belum Check-in", BELUM_PULANG: "Belum Pulang",
};

const ATT_KEYS = ["HADIR","TERLAMBAT","BELUM_CHECKIN","BELUM_PULANG","IZIN","SAKIT","CUTI","ALPA","PERLU_VERIFIKASI"];
// Kartu lokasi: kolom kiri kehadiran, kolom kanan ketidakhadiran.
const LOC_COLS = [
  ["HADIR","TERLAMBAT","BELUM_CHECKIN","BELUM_PULANG","PERLU_VERIFIKASI"],
  ["IZIN","SAKIT","CUTI","ALPA"],
];

// Accent per status — palet yang sama dengan Dashboard Utama.
const ATT_TONE = {
  HADIR:            { accent: "#16A34A", Icon: UserCheck },
  TERLAMBAT:        { accent: "#D97706", Icon: Clock },
  BELUM_CHECKIN:    { accent: "#DC2626", Icon: LogIn },
  BELUM_PULANG:     { accent: "#0055FF", Icon: LogOut },
  IZIN:             { accent: "#7C3AED", Icon: FileText },
  SAKIT:            { accent: "#DB2777", Icon: HeartPulse },
  CUTI:             { accent: "#0284C7", Icon: CalendarDays },
  ALPA:             { accent: "#4B5563", Icon: XCircle },
  PERLU_VERIFIKASI: { accent: "#E11D48", Icon: AlertTriangle },
};

const OV_KEYS = ["IN_PROGRESS","PERLU_VERIFIKASI","SELESAI","CANCELLED"];
const OV_LABEL = {
  IN_PROGRESS: "Berlangsung", PERLU_VERIFIKASI: "Perlu Verifikasi",
  SELESAI: "Selesai", CANCELLED: "Dibatalkan",
};
const OV_TONE = {
  IN_PROGRESS:      { accent: "#0055FF", Icon: PlayCircle },
  PERLU_VERIFIKASI: { accent: "#DC2626", Icon: AlarmClock },
  SELESAI:          { accent: "#16A34A", Icon: CheckCircle2 },
  CANCELLED:        { accent: "#6B7280", Icon: Ban },
};

const CARD = "bg-white border border-[#E5E7EB] rounded-xl shadow-[0_1px_2px_rgba(16,24,40,0.04)]";

const pctOf = (val, total) => (total > 0 ? Math.min(100, Math.round((val / total) * 100)) : 0);

// BELUM_PULANG adalah subset HADIR/TERLAMBAT → tidak ikut dijumlah.
const peopleOf = (counts) => ATT_KEYS.filter((k) => k !== "BELUM_PULANG").reduce((s, k) => s + (counts[k] || 0), 0);

const kpiKeyOf = (k) => k.replace("BELUM_CHECKIN", "belum-checkin")
  .replace("BELUM_PULANG", "belum-pulang")
  .replace("PERLU_VERIFIKASI", "perlu-verifikasi")
  .toLowerCase();

const fmtHHmm = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Jakarta", hour12: false });
  } catch { return "—"; }
};

// Kartu statistik kompak; menjadi <button> hanya bila onClick diberikan.
function StatCard({ tone, label, val, pct, onClick, testId, valueTestId, ariaLabel }) {
  const { accent, Icon = Users } = tone;
  const Tag = onClick ? "button" : "div";
  const clickProps = onClick ? { type: "button", onClick, "aria-label": ariaLabel } : {};
  return (
    <Tag
      {...clickProps}
      data-testid={testId}
      title={label}
      className={`group relative w-full min-w-0 flex flex-col text-left rounded-xl border px-2.5 py-2 transition-[transform,box-shadow] duration-200 ${onClick ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-md active:scale-[0.985] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0055FF]" : ""}`}
      style={{ background: accent + "0A", borderColor: accent + "33" }}
    >
      {onClick && <ChevronRight className="absolute top-1.5 right-1.5 h-3 w-3 text-[#9CA3AF] opacity-60 group-hover:opacity-100 transition-opacity" />}
      <div className="flex items-center gap-2 min-w-0 pr-2">
        <span className="h-7 w-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: accent + "1A", color: accent }}>
          <Icon className="h-3.5 w-3.5" />
        </span>
        <span data-testid={valueTestId} className="font-display text-xl leading-none font-black tracking-tighter font-mono-tab text-[#0A0A0A]">{val}</span>
      </div>
      <div className="mt-1.5 text-[9.5px] leading-tight font-bold uppercase tracking-[0.04em]" style={{ color: accent }}>{label}</div>
      <div className="mt-auto pt-1.5 flex items-center gap-1.5">
        <div className="flex-1 h-1 rounded-full bg-[#E5E7EB]/70 overflow-hidden">
          <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${pct}%`, background: accent }} />
        </div>
        <span className="text-[9px] font-bold leading-none font-mono-tab" style={{ color: pct > 0 ? accent : "#9CA3AF" }}>{pct}%</span>
      </div>
    </Tag>
  );
}

function Panel({ icon: Icon, title, badge, action, testId, children }) {
  return (
    <section className={`${CARD} px-3 sm:px-4 py-3`} data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2.5">
        <h2 className="flex items-center gap-2 min-w-0 font-display text-sm sm:text-base font-black tracking-tight text-[#0A0A0A]">
          <span className="h-7 w-7 rounded-lg flex items-center justify-center flex-shrink-0 bg-[#0055FF]/10 text-[#0055FF]"><Icon className="h-3.5 w-3.5" /></span>
          <span className="min-w-0 leading-tight">{title}</span>
          {badge != null && (
            <span className="text-[10px] font-bold text-[#6B7280] uppercase tracking-widest bg-[#F3F4F6] rounded-md px-1.5 py-0.5 whitespace-nowrap">{badge}</span>
          )}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

const PERLU_LINK = "text-xs font-semibold text-[#DC2626] hover:underline whitespace-nowrap";

export default function Operations() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/operations/dashboard");
      setData(data);
      setLastRefresh(new Date(data.generated_at));
    } catch (e) {
      const msg = e.response?.data?.detail || "Gagal memuat dashboard operasional";
      if (!silent) toast.error(typeof msg === "string" ? msg : "Gagal memuat");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Manual refresh — 60s auto-refresh untuk monitoring ringan. Backend
  // agregasi query cukup murah (2 collections filter by today + cohort),
  // tetapi tetap batasi frekuensi supaya tidak beban di deployment.
  useEffect(() => {
    const t = setInterval(() => load(true), 60000);
    return () => clearInterval(t);
  }, [load]);

  if (loading && !data) {
    return (
      <div className="p-6 flex items-center justify-center h-64" data-testid="ops-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#4B5563]" />
      </div>
    );
  }
  // Teknisi (or any non-admin/koord user landing here manually via URL)
  // gets an explicit empty state — backend already returned 403 and the
  // toast fired, but we render a friendly page instead of blank content.
  if (!data) {
    return (
      <div className="p-6" data-testid="ops-access-denied">
        <div className="max-w-md mx-auto rounded-lg border border-[#E5E7EB] bg-white p-6 text-center">
          <AlertTriangle className="h-8 w-8 text-[#991B1B] mx-auto mb-3" />
          <h1 className="font-display text-lg font-bold text-[#0A0A0A] mb-1">Akses Ditolak</h1>
          <p className="text-sm text-[#6B7280] mb-4">Halaman Absensi & Lembur hanya tersedia untuk Admin & Koordinator.</p>
          <button
            onClick={() => navigate("/")}
            className="h-9 px-4 rounded-md bg-[#065F46] hover:bg-[#064E3B] text-white text-sm font-bold"
            data-testid="ops-back-dashboard"
          >
            Kembali ke Dashboard
          </button>
        </div>
      </div>
    );
  }

  const att = data.attendance;
  const attKoord = data.attendance_koord || {};
  const koordCohort = data.koord_cohort_size || 0;
  const ov = data.overtime;
  const today = data.date;
  const ovTotal = OV_KEYS.reduce((s, k) => s + (ov[k] || 0), 0);
  const hasNoLocation = data.per_location.some((l) => l.location_id === null);

  const attGrid = (counts, cohort, role, prefix, kpiPrefix) => (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9 gap-2">
      {ATT_KEYS.map((k) => {
        const val = counts[k] || 0;
        return (
          <StatCard
            key={k}
            tone={ATT_TONE[k]}
            label={ATT_LABEL[k]}
            val={val}
            pct={pctOf(val, cohort)}
            testId={`${prefix}${k}`}
            valueTestId={`${kpiPrefix}${kpiKeyOf(k)}`}
            ariaLabel={`Lihat detail ${ATT_LABEL[k]} ${role}, total ${val}`}
            onClick={() => navigate(`/attendance-report?start_date=${today}&end_date=${today}&role=${role}&status=${k}`)}
          />
        );
      })}
    </div>
  );

  return (
    <div className="space-y-3" data-testid="ops-dashboard">
      {/* Header */}
      <div className={`${CARD} px-3 sm:px-4 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-2`} data-testid="ops-header">
        <div className="flex items-center gap-3 min-w-0 flex-1 basis-[240px]">
          <span className="h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-[#0055FF]/10 text-[#0055FF]"><Users className="h-5 w-5" /></span>
          <div className="min-w-0">
            <h1 className="font-display text-xl sm:text-2xl font-black tracking-tighter leading-tight text-[#0A0A0A]">Absensi & Lembur</h1>
            <p className="text-[11px] sm:text-xs text-[#6B7280] sm:truncate" data-testid="ops-subtitle">Pantau kehadiran dan lembur teknisi & koordinator</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="h-8 inline-flex items-center gap-1.5 px-2.5 rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] text-xs font-semibold text-[#374151] whitespace-nowrap" data-testid="ops-date">
            <CalendarDays className="h-3.5 w-3.5 text-[#6B7280]" /> {today} (WIB)
          </span>
          <span className="h-8 inline-flex items-center px-2.5 rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] text-xs text-[#374151] whitespace-nowrap" data-testid="ops-cohort">
            Cohort <b className="mx-1 font-mono-tab">{data.cohort_size}</b> teknisi · <b className="mx-1 font-mono-tab">{koordCohort}</b> koordinator
          </span>
          {lastRefresh && (
            <span className="text-[11px] text-[#6B7280] flex items-center gap-1 whitespace-nowrap" data-testid="ops-last-refresh">
              Terakhir: <span className="font-semibold text-[#0A0A0A] font-mono-tab">{fmtHHmm(lastRefresh.toISOString())}</span>
            </span>
          )}
          <button
            onClick={() => load()}
            disabled={loading}
            data-testid="ops-refresh"
            className="h-8 px-3 rounded-lg border border-[#E5E7EB] bg-white hover:bg-[#F9FAFB] text-xs font-semibold text-[#0055FF] flex items-center gap-1.5 disabled:opacity-50 transition-colors"
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5" />}
            Refresh
          </button>
          <button
            onClick={() => navigate(`/attendance-report?start_date=${today}&end_date=${today}`)}
            className="h-8 px-3 rounded-lg border border-[#0055FF]/20 bg-[#0055FF]/10 hover:bg-[#0055FF]/15 text-xs font-semibold text-[#0055FF] flex items-center gap-1.5 transition-colors whitespace-nowrap"
            data-testid="ops-nav-attendance"
          >
            <ClipboardList className="h-3.5 w-3.5" /> Lihat Rekap Absensi <ArrowRight className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => navigate(`/overtime?start_date=${today}&end_date=${today}`)}
            className="h-8 px-3 rounded-lg border border-[#16A34A]/20 bg-[#16A34A]/10 hover:bg-[#16A34A]/15 text-xs font-semibold text-[#15803D] flex items-center gap-1.5 transition-colors whitespace-nowrap"
            data-testid="ops-nav-overtime"
          >
            <Timer className="h-3.5 w-3.5" /> Rekap Lembur <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Absensi Teknisi — setiap kartu navigate ke Rekap Absensi (role=teknisi + status). */}
      <Panel
        icon={Wrench}
        title="Absensi Teknisi Hari Ini"
        badge={`Cohort ${data.cohort_size}`}
        testId="ops-att-section"
        action={(att.PERLU_VERIFIKASI || 0) > 0 && (
          <button
            onClick={() => navigate(`/attendance-report?start_date=${today}&end_date=${today}&status=PERLU_VERIFIKASI`)}
            className={PERLU_LINK}
            data-testid="ops-nav-attendance-perlu"
          >
            Perlu Verifikasi →
          </button>
        )}
      >
        {attGrid(att, data.cohort_size, "teknisi", "ops-att-", "attendance-kpi-tech-")}
      </Panel>

      {/* Absensi Koordinator — hanya render bila cohort koordinator > 0. */}
      {koordCohort > 0 && (
        <Panel icon={UserCog} title="Absensi Koordinator Hari Ini" badge={`Cohort ${koordCohort}`} testId="ops-att-koord-section">
          {attGrid(attKoord, koordCohort, "koordinator", "ops-att-koord-", "attendance-kpi-koord-")}
        </Panel>
      )}

      {/* Lembur Hari Ini */}
      <Panel
        icon={Clock}
        title="Lembur Hari Ini"
        testId="ops-ov-section"
        action={(
          <button
            onClick={() => navigate(`/overtime?start_date=${today}&end_date=${today}&status=PERLU_VERIFIKASI`)}
            className={PERLU_LINK}
            data-testid="ops-nav-overtime-perlu"
          >
            Perlu Verifikasi →
          </button>
        )}
      >
        <div className="grid grid-cols-2 lg:grid-cols-[repeat(4,minmax(0,1fr))_minmax(0,1.35fr)] gap-2">
          {OV_KEYS.map((k) => (
            <StatCard
              key={k}
              tone={OV_TONE[k]}
              label={OV_LABEL[k]}
              val={ov[k] || 0}
              pct={pctOf(ov[k] || 0, ovTotal)}
              testId={`ops-ov-${k}`}
            />
          ))}
          <div className="col-span-2 lg:col-span-1 rounded-xl border border-[#0055FF]/20 bg-[#0055FF]/[0.06] px-3 py-2 flex items-center gap-2.5 min-w-0" data-testid="ops-ov-duration">
            <span className="h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-[#0055FF]/10 text-[#0055FF]"><Timer className="h-4 w-4" /></span>
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-[0.06em] text-[#0055FF] leading-tight">Total Durasi Lembur Hari Ini</div>
              <div className="font-display text-base xl:text-lg leading-tight font-black tracking-tight font-mono-tab text-[#0A0A0A] whitespace-nowrap" data-testid="ops-ov-duration-value">
                {ov.total_duration_human}
              </div>
            </div>
          </div>
        </div>
      </Panel>

      {/* Laporan per Lokasi — setiap angka clickable ke Rekap (lokasi + role + status). */}
      <Panel
        icon={MapPin}
        title="Laporan per Lokasi"
        testId="ops-loc-section"
        action={hasNoLocation && user?.role === "admin" && (
          // Quick-action bucket Tanpa Lokasi — admin-only, hanya bila bucket ada.
          <button
            onClick={() => navigate("/users?filter=no-location")}
            className="h-8 px-3 rounded-lg border border-[#E5E7EB] bg-white hover:bg-[#F9FAFB] text-xs font-semibold text-[#0055FF] flex items-center gap-1.5 whitespace-nowrap transition-colors"
            data-testid="ops-loc-no-location-cta"
          >
            <Users className="h-3.5 w-3.5" /> Lihat User Tanpa Lokasi
          </button>
        )}
      >
        {data.per_location.length === 0 ? (
          <div className="rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] p-4 text-center text-sm text-[#6B7280]">
            Tidak ada lokasi dalam cakupan Anda.
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-2.5">
            {data.per_location.map((l) => {
              const kc = l.counts_koord || {};
              const koordTotal = ATT_KEYS.reduce((s, k) => s + (kc[k] || 0), 0);
              const code = l.location_code || "no-location";
              const loc = l.location_id ? `&location_id=${l.location_id}` : "";
              // Indikator visual saja: Belum Check-in >= 50% per kelompok (cohort 0 tidak ditandai).
              const highGroups = [["Teknisi", l.counts || {}], ["Koordinator", kc]]
                .map(([name, c]) => ({ name, people: peopleOf(c), pct: pctOf(c.BELUM_CHECKIN || 0, peopleOf(c)) }))
                .filter((g) => g.people > 0 && g.pct >= 50);
              const isHigh = highGroups.length > 0;
              const renderBlock = (counts, role, koord) => {
                const people = peopleOf(counts);
                return (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4" data-testid={koord ? `ops-loc-${code}-koord` : undefined}>
                    {LOC_COLS.map((col, ci) => (
                      <div key={ci} className="min-w-0">
                        {col.map((k) => {
                          const val = counts[k] || 0;
                          const pct = pctOf(val, people);
                          const accent = ATT_TONE[k].accent;
                          return (
                            <button
                              key={k}
                              type="button"
                              data-testid={`ops-loc-${code}-${koord ? "koord" : "teknisi"}-${k}`}
                              aria-label={`Rekap ${ATT_LABEL[k]} ${role} di ${l.location_name}, total ${val}`}
                              onClick={() => navigate(`/attendance-report?start_date=${today}&end_date=${today}&role=${role}&status=${k}${loc}`)}
                              className="w-full flex items-center gap-1.5 rounded-md px-1.5 h-6 hover:bg-[#F3F4F6] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0055FF]"
                            >
                              <span className="h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ background: accent }} />
                              <span className="text-[11px] text-[#4B5563] truncate flex-1 min-w-0 text-left">{ATT_LABEL[k]}</span>
                              <span className={`w-5 text-right text-xs font-black font-mono-tab ${val > 0 ? "text-[#0A0A0A]" : "text-[#9CA3AF]"}`}>{val}</span>
                              <span className="hidden sm:block w-10 2xl:w-16 h-1 rounded-full bg-[#E5E7EB] overflow-hidden flex-shrink-0">
                                <span className="block h-full rounded-full" style={{ width: `${pct}%`, background: accent }} />
                              </span>
                              <span className="w-7 text-right text-[10px] font-mono-tab" style={{ color: val > 0 ? accent : "#9CA3AF" }}>{pct}%</span>
                            </button>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                );
              };
              return (
                <div key={l.location_id || "__no_loc__"} data-testid={`ops-loc-${code}`} data-high-belum-checkin={isHigh ? "true" : "false"} className={`rounded-xl border p-3 min-w-0 transition-colors ${isHigh ? "border-[#DC2626]/30 bg-[#DC2626]/[0.03] shadow-[inset_3px_0_0_#DC2626]" : "border-[#E5E7EB] bg-white"}`}>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-2 pb-2 border-b border-[#F3F4F6]">
                    <MapPin className="h-4 w-4 text-[#0055FF] flex-shrink-0" />
                    <div className="font-bold text-sm truncate min-w-0">{l.location_name}</div>
                    {l.location_code && <span className="text-[10px] font-semibold text-[#0055FF] bg-[#0055FF]/10 rounded px-1.5 py-0.5 font-mono whitespace-nowrap flex-shrink-0">{l.location_code}</span>}
                    {isHigh && (
                      <span
                        className="flex items-center gap-1 text-[10px] font-bold text-[#DC2626] bg-[#DC2626]/10 rounded-full px-2 py-0.5 whitespace-nowrap flex-shrink-0"
                        title={`Belum Check-in tinggi: ${highGroups.map((g) => `${g.name} ${g.pct}%`).join(", ")}`}
                        data-testid={`ops-loc-${code}-high-flag`}
                      >
                        <AlertTriangle className="h-3 w-3" />
                        <span className="hidden 2xl:inline">Perlu Perhatian ·</span> {highGroups.map((g) => `${g.name} ${g.pct}%`).join(" · ")}
                      </span>
                    )}
                    <span className="ml-auto flex items-center gap-1 text-[11px] font-bold text-[#0055FF] bg-[#0055FF]/10 rounded-full px-2 py-0.5 whitespace-nowrap flex-shrink-0" title="Lembur Berlangsung" data-testid={`ops-loc-${code}-lembur`}>
                      <Timer className="h-3 w-3" /> <span className={isHigh ? "hidden 2xl:inline" : "hidden sm:inline"}>Lembur Berlangsung</span><span className={isHigh ? "2xl:hidden" : "sm:hidden"}>Lembur</span> <span className="font-mono-tab">{l.lembur_berlangsung || 0}</span>
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[11px] font-bold text-[#0A0A0A] mb-0.5" data-testid={`ops-loc-${code}-teknisi-label`}>
                    <Wrench className="h-3 w-3 text-[#6B7280]" /> Teknisi
                  </div>
                  {renderBlock(l.counts, "teknisi", false)}
                  {koordTotal > 0 && (
                    <>
                      <div className="flex items-center gap-1.5 text-[11px] font-bold text-[#0A0A0A] mt-2 mb-0.5" data-testid={`ops-loc-${code}-koord-label`}>
                        <UserCog className="h-3 w-3 text-[#6B7280]" /> Koordinator
                      </div>
                      {renderBlock(kc, "koordinator", true)}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Panel>
    </div>
  );
}
